import { useRef } from 'react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsyncFn from 'react-use/esm/useAsyncFn';
import { useMountEffect } from '@react-hookz/web';

/**
 * Hook used for storing the full entity of the specified owners
 * in order to display users and group using the information contained on each entity.
 * When a component is rendered for the first time, it loads the content of the entities
 * specified by `initialSelectedOwnersRefs` and export the `getEntity` and `setEntity`
 * utilities, used to retrieve and modify the owners.
 */
export function useSelectedOwners({
  enabled,
  initialSelectedOwnersRefs,
}: {
  enabled: boolean;
  initialSelectedOwnersRefs: string[];
}) {
  const allEntities = useRef<Record<string, Entity>>({});
  const catalogApi = useApi(catalogApiRef);

  const [, handleFetch] = useAsyncFn(async () => {
    const initialSelectedEntities = await catalogApi.getEntitiesByRefs({
      entityRefs: initialSelectedOwnersRefs,
    });
    initialSelectedEntities.items.forEach(e => {
      if (e) {
        allEntities.current[stringifyEntityRef(e)] = e;
      }
    });
  }, []);

  useMountEffect(() => {
    if (enabled && initialSelectedOwnersRefs.length > 0) {
      handleFetch();
    }
  });

  return {
    getEntity: (entityRef: string) => allEntities.current[entityRef],
    setEntity: (entity: Entity) => {
      allEntities.current[stringifyEntityRef(entity)] = entity;
    },
  };
}
